import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Collapse,
  ClickAwayListener,
  Alert,
  FormHelperText,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

type FeedbackType = 'question' | 'complaint' | 'feature' | 'other';

interface FeedbackFormValues {
  type: FeedbackType;
  message: string;
}

/**
 * Tester Feedback Bubble Component
 *
 * A persistent feedback widget that appears in the bottom-left corner
 * for tester accounts only. Allows testers to submit questions, complaints,
 * feature requests, and other feedback directly from any page.
 *
 * Features:
 * - Conditional rendering (testers only)
 * - Expandable from collapsed bubble to full form
 * - Form validation (10-1000 chars)
 * - Click-away and ESC key to close
 * - Success/error feedback with auto-collapse
 */
export const TesterFeedbackBubble: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Form validation schema
  const validationSchema = yup.object({
    type: yup
      .string()
      .oneOf(['question', 'complaint', 'feature', 'other'])
      .required(t('testerFeedback.validation.required')),
    message: yup
      .string()
      .min(10, t('testerFeedback.validation.minLength'))
      .max(1000, t('testerFeedback.validation.maxLength'))
      .required(t('testerFeedback.validation.required')),
  });

  // Form handling
  const formik = useFormik<FeedbackFormValues>({
    initialValues: {
      type: 'question',
      message: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await apiService.testerFeedback.submit({
          type: values.type,
          message: values.message,
        });

        // Show success message
        setSubmitStatus({
          type: 'success',
          message: t('testerFeedback.success'),
        });

        // Reset form
        resetForm();

        // Auto-collapse after 2 seconds
        setTimeout(() => {
          setIsExpanded(false);
          setSubmitStatus({ type: null, message: '' });
        }, 2000);
      } catch (error: any) {
        console.error('Failed to submit feedback:', error);
        setSubmitStatus({
          type: 'error',
          message: error.message || t('testerFeedback.error'),
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    // Clear status when opening
    if (!isExpanded) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setSubmitStatus({ type: null, message: '' });
  };

  const handleClickAway = () => {
    if (isExpanded && !formik.isSubmitting) {
      handleClose();
    }
  };

  // Handle ESC key to close
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded && !formik.isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded, formik.isSubmitting]);

  // Don't render if not a test account (after all hooks)
  if (!user?.isTestAccount) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        {/* Collapsed bubble button */}
        <IconButton
          onClick={handleToggle}
          aria-label={t('testerFeedback.buttonLabel')}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            zIndex: 1300,
            backgroundColor: '#000',
            color: '#fff',
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            boxShadow: 3,
            '&:hover': {
              backgroundColor: '#333',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ChatBubbleOutlineIcon />
        </IconButton>

        {/* Expanded form card */}
        <Collapse in={isExpanded}>
          <Paper
            elevation={6}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, sm: 96 },
              left: { xs: 16, sm: 24 },
              zIndex: 1300,
              width: { xs: 'calc(100vw - 32px)', sm: 400 },
              maxWidth: '90vw',
              p: 3,
              backgroundColor: 'background.paper',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('testerFeedback.title')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('testerFeedback.subtitle')}
                </Typography>
              </Box>
              <IconButton onClick={handleClose} size="small" aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Status alerts */}
            {submitStatus.type && (
              <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
                {submitStatus.message}
              </Alert>
            )}

            {/* Feedback form */}
            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Type selector */}
                <FormControl
                  fullWidth
                  size="small"
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <InputLabel id="feedback-type-label">
                    {t('testerFeedback.typeLabel')}
                  </InputLabel>
                  <Select
                    labelId="feedback-type-label"
                    id="type"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={t('testerFeedback.typeLabel')}
                  >
                    <MenuItem value="question">
                      {t('testerFeedback.types.question')}
                    </MenuItem>
                    <MenuItem value="complaint">
                      {t('testerFeedback.types.complaint')}
                    </MenuItem>
                    <MenuItem value="feature">
                      {t('testerFeedback.types.feature')}
                    </MenuItem>
                    <MenuItem value="other">
                      {t('testerFeedback.types.other')}
                    </MenuItem>
                  </Select>
                  {formik.touched.type && formik.errors.type && (
                    <FormHelperText>{formik.errors.type}</FormHelperText>
                  )}
                </FormControl>

                {/* Message textarea */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="message"
                  name="message"
                  label={t('testerFeedback.messageLabel')}
                  placeholder={t('testerFeedback.messagePlaceholder')}
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.message && Boolean(formik.errors.message)}
                  helperText={formik.touched.message && formik.errors.message}
                  inputProps={{
                    maxLength: 1000,
                  }}
                />

                {/* Character count */}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {formik.values.message.length} / 1000
                </Typography>

                {/* Disclaimer */}
                <Typography variant="caption" color="warning.main">
                  {t('testerFeedback.disclaimer')}
                </Typography>

                {/* Submit button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={formik.isSubmitting || !formik.isValid}
                  startIcon={formik.isSubmitting ? null : <SendIcon />}
                  sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#333',
                    },
                  }}
                >
                  {formik.isSubmitting
                    ? t('testerFeedback.submitting')
                    : t('testerFeedback.submit')}
                </Button>
              </Box>
            </form>
          </Paper>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
};

export default TesterFeedbackBubble;
